import { BoatState } from '../types';

export function renderBoat(ctx: CanvasRenderingContext2D, boat: BoatState, scale: number = 0.6) {
  ctx.save();

  ctx.translate(boat.position.x, boat.position.y);
  ctx.rotate(boat.heading);
  ctx.scale(scale, scale);

  const heelSkew = boat.heelAngle * 0.4;
  ctx.transform(1, 0, heelSkew, 1, 0, 0);

  // Hull
  ctx.beginPath();
  ctx.moveTo(0, -25);
  ctx.bezierCurveTo(12, -15, 12, 15, 8, 22);
  ctx.lineTo(-8, 22);
  ctx.bezierCurveTo(-12, 15, -12, -15, 0, -25);
  ctx.closePath();

  const hullGradient = ctx.createLinearGradient(-10, 0, 10, 0);
  hullGradient.addColorStop(0, '#5D3A1A');
  hullGradient.addColorStop(0.5, '#8B5A2B');
  hullGradient.addColorStop(1, '#5D3A1A');
  ctx.fillStyle = hullGradient;
  ctx.fill();

  ctx.strokeStyle = '#3D2A0A';
  ctx.lineWidth = 2;
  ctx.stroke();

  // Deck line
  ctx.beginPath();
  ctx.moveTo(-6, 18);
  ctx.lineTo(6, 18);
  ctx.strokeStyle = '#4A3520';
  ctx.lineWidth = 3;
  ctx.stroke();

  // Rudder
  ctx.save();
  ctx.translate(0, 22);
  ctx.rotate(boat.rudderAngle * 0.8);

  ctx.beginPath();
  ctx.moveTo(-2, 0);
  ctx.lineTo(-1, 12);
  ctx.lineTo(1, 12);
  ctx.lineTo(2, 0);
  ctx.closePath();
  ctx.fillStyle = '#3D2A0A';
  ctx.fill();

  ctx.restore();

  // Mast
  ctx.beginPath();
  ctx.moveTo(0, -20);
  ctx.lineTo(0, 5);
  ctx.strokeStyle = '#2C1810';
  ctx.lineWidth = 3;
  ctx.stroke();

  // Sail - improved wind-reactive rendering
  renderSail(ctx, boat);

  ctx.restore();
}

function renderSail(ctx: CanvasRenderingContext2D, boat: BoatState) {
  ctx.save();
  ctx.translate(0, -8);

  const absWindAngle = Math.abs(boat.apparentWindAngle);
  const windFromRight = boat.apparentWindAngle > 0;

  // Determine sail behavior based on point of sail
  const isInIrons = boat.pointOfSail === 'inIrons';
  const isCloseHauled = boat.pointOfSail === 'closeHauled';
  const isRunning = boat.pointOfSail === 'running';

  // Calculate sail rotation angle
  // Sail swings out more as wind comes from behind
  let sailRotation: number;
  if (isInIrons) {
    // Luffing - sail gently sways near centerline
    const luffAmount = Math.sin(Date.now() * 0.003) * 0.08;
    sailRotation = luffAmount;
  } else if (isRunning) {
    // Running - sail nearly perpendicular to boat
    sailRotation = windFromRight ? Math.PI / 2.2 : -Math.PI / 2.2;
  } else {
    // Normal sailing - sail angle based on apparent wind
    const baseAngle = Math.min(absWindAngle * 0.5, Math.PI / 2.5);
    sailRotation = windFromRight ? baseAngle : -baseAngle;
  }

  ctx.rotate(sailRotation);

  // Calculate sail curve (belly) based on wind
  // More wind = more curve, but it saturates
  const windPressure = Math.min(boat.apparentWindSpeed / 15, 1);

  let curveMagnitude: number;
  let curveDirection: number; // 1 = curve right, -1 = curve left

  if (isInIrons) {
    // Luffing sail - gentle sway, minimal curve
    curveMagnitude = 4 + Math.sin(Date.now() * 0.004) * 1.5;
    curveDirection = Math.sin(Date.now() * 0.003) > 0 ? 1 : -1;
  } else {
    // Wind fills sail from one side
    // Curve away from wind direction (wind pushes sail out)
    curveDirection = windFromRight ? -1 : 1;

    // Curve amount based on point of sail
    if (isCloseHauled) {
      // Close hauled - flatter sail for pointing ability
      curveMagnitude = 6 + windPressure * 4;
    } else if (isRunning) {
      // Running - sail is out, less apparent curve visible from above
      curveMagnitude = 8 + windPressure * 4;
    } else {
      // Reaching - full belly
      curveMagnitude = 10 + windPressure * 6;
    }
  }

  const curve = curveMagnitude * curveDirection;

  // Draw main sail shape
  ctx.beginPath();
  ctx.moveTo(0, 0); // Boom attachment point

  // Sail leech (back edge) - curves with the wind
  ctx.bezierCurveTo(
    curve * 0.3, -12,      // First control point
    curve * 1.0, -25,      // Second control point - maximum belly
    0, -38                  // Head of sail
  );

  // Sail luff (front edge) - relatively straight, attached to mast
  ctx.bezierCurveTo(
    curve * 0.15, -28,     // Slight curve
    curve * 0.1, -12,
    0, 0                    // Back to boom
  );

  // Sail fill with gradient showing depth
  if (isInIrons) {
    // Luffing - grey, transparent
    ctx.fillStyle = 'rgba(200, 200, 200, 0.5)';
  } else {
    // Create gradient to show 3D curve of sail
    const gradientStart = curve > 0 ? -2 : 2;
    const gradientEnd = curve > 0 ? curve + 2 : curve - 2;
    const sailGradient = ctx.createLinearGradient(gradientStart, -20, gradientEnd, -20);

    if (curve > 0) {
      // Wind from left, sail curves right
      sailGradient.addColorStop(0, 'rgba(255, 255, 255, 0.98)');
      sailGradient.addColorStop(0.4, 'rgba(250, 250, 250, 0.95)');
      sailGradient.addColorStop(1, 'rgba(220, 220, 225, 0.9)');
    } else {
      // Wind from right, sail curves left
      sailGradient.addColorStop(0, 'rgba(220, 220, 225, 0.9)');
      sailGradient.addColorStop(0.6, 'rgba(250, 250, 250, 0.95)');
      sailGradient.addColorStop(1, 'rgba(255, 255, 255, 0.98)');
    }
    ctx.fillStyle = sailGradient;
  }

  ctx.fill();

  // Sail outline
  ctx.strokeStyle = isInIrons ? '#999' : '#666';
  ctx.lineWidth = 1;
  ctx.stroke();

  // Battens (horizontal stiffeners) - curve with the sail
  ctx.strokeStyle = 'rgba(100, 100, 100, 0.4)';
  ctx.lineWidth = 0.8;

  // Upper batten
  ctx.beginPath();
  ctx.moveTo(0, -28);
  ctx.quadraticCurveTo(curve * 0.4, -28, curve * 0.15, -28);
  ctx.stroke();

  // Middle batten
  ctx.beginPath();
  ctx.moveTo(0, -18);
  ctx.quadraticCurveTo(curve * 0.6, -18, curve * 0.2, -18);
  ctx.stroke();

  // Lower batten
  ctx.beginPath();
  ctx.moveTo(0, -8);
  ctx.quadraticCurveTo(curve * 0.4, -8, curve * 0.1, -8);
  ctx.stroke();

  // Boom (horizontal spar at bottom of sail)
  ctx.beginPath();
  ctx.moveTo(0, 0);
  ctx.lineTo(curve * 0.3, 0);
  ctx.strokeStyle = '#2C1810';
  ctx.lineWidth = 2.5;
  ctx.stroke();

  ctx.restore();
}

export function renderWake(ctx: CanvasRenderingContext2D, boat: BoatState, scale: number = 0.6) {
  if (boat.speed < 1) return;

  ctx.save();
  ctx.translate(boat.position.x, boat.position.y);
  ctx.rotate(boat.heading);
  ctx.scale(scale, scale);

  const wakeLength = Math.min(boat.speed * 8, 60);
  const wakeWidth = Math.min(boat.speed * 2, 15);

  const gradient = ctx.createLinearGradient(0, 22, 0, 22 + wakeLength);
  gradient.addColorStop(0, 'rgba(255, 255, 255, 0.4)');
  gradient.addColorStop(0.3, 'rgba(255, 255, 255, 0.2)');
  gradient.addColorStop(1, 'rgba(255, 255, 255, 0)');

  ctx.beginPath();
  ctx.moveTo(-3, 22);
  ctx.lineTo(-wakeWidth, 22 + wakeLength);
  ctx.lineTo(wakeWidth, 22 + wakeLength);
  ctx.lineTo(3, 22);
  ctx.closePath();

  ctx.fillStyle = gradient;
  ctx.fill();

  ctx.restore();
}
